import type { FieldErrors } from 'react-hook-form';
import { Controller, useForm, FormProvider } from 'react-hook-form';

import { FormProgressStepper } from '~widgets/application-form/form-progress-stepper';

import { AddressSuggestion } from '~entities/address/ui';

import { useFormInitialize } from '~shared/hooks';
import { combineValidators } from '~shared/lib/combine-validators';
import { getFieldsErrors } from '~shared/lib/get-fields-errors';
import { FormFooter } from '~shared/ui/form-footer';
import { HeaderWithLogo } from '~shared/ui/header-with-logo';
import { Screen } from '~shared/ui/screen';
import { ScreenTitle } from '~shared/ui/screen-title';
import { validators } from '~shared/validators';

import { Input } from '@breeze/rhf-adapters';

import { STREET_HOUSE_FIELD_NAME, UNIT_FLOOR_FIELD_NAME } from '../constants';
import type { FormValues } from '../types';

const fieldNames = {
  country: 'country',
  region: 'region',
  postalCode: 'postalCode',
  province: 'province',
  cityOrMunicipality: 'cityOrMunicipality',
  barangay: 'barangay',
};

type Props = {
  initialValue?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
  onPrev: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
  onFieldCompleted?: (
    values: Partial<FormValues>,
    errors: FieldErrors<FormValues>
  ) => void;
};

export const PermanentAddress: React.FC<Props> = (props) => {
  const { initialValue = {}, onSubmit, onFieldCompleted, onPrev } = props;

  const methods = useForm<FormValues>({
    mode: 'all',
    defaultValues: initialValue,
  });
  const { getValues, handleSubmit, control } = methods;

  useFormInitialize(methods, initialValue);

  const handleOnChange = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onFieldCompleted?.(getValues(), actualErrors);
  };

  const handlePrevClick = () => {
    const actualErrors = getFieldsErrors<FormValues>(methods);

    onPrev(getValues(), actualErrors);
  };

  return (
    <form onBlur={handleOnChange} onSubmit={handleSubmit(onSubmit)}>
      <Screen
        header={<HeaderWithLogo />}
        footer={<FormFooter onClickPrev={handlePrevClick} />}
      >
        <div className="mb-5">
          <ScreenTitle
            title="What’s your permanent address?"
            subtitle="The address in&nbsp;your legal documents"
            icon={<FormProgressStepper step="home" />}
          />
        </div>
        <FormProvider {...methods}>
          <>
            <AddressSuggestion fieldNames={fieldNames} />
            <Controller
              name="streetAndHouse"
              control={control}
              rules={{
                required: 'Enter your house number and street name',
                validate: combineValidators(
                  validators.maxLength({ maxLength: 150 }),
                  validators.filipinoAddress()
                ),
              }}
              render={(fieldProps) => (
                <Input
                  label={STREET_HOUSE_FIELD_NAME}
                  // chrome ignores autocomplete=off for fields with common names(like province, password, etc)
                  // -> we need to provide nonexistent autoComplete to turn it off
                  autoComplete="permanent-address-house"
                  {...fieldProps}
                />
              )}
            />
            <Controller
              name="unitAndFloor"
              control={control}
              rules={{
                validate: combineValidators(
                  validators.maxLength({ maxLength: 150 }),
                  validators.filipinoAddress()
                ),
              }}
              render={(fieldProps) => (
                <Input
                  label={UNIT_FLOOR_FIELD_NAME}
                  autoComplete="permanent-address-apartment"
                  {...fieldProps}
                />
              )}
            />
          </>
        </FormProvider>
      </Screen>
    </form>
  );
};
